import { Injectable } from '@nestjs/common';
import { InjectModel } from '@nestjs/mongoose';
import { Model, Types } from 'mongoose';
import { Order, OrderDocument } from '@/modules/order/schemas/order.schema';
import { Product, ProductDocument } from '@/modules/product/schemas/product.schema';
import { Package, PackageDocument } from '@/modules/package/schemas/package.schema';
import { Expense, ExpenseDocument, ExpenseStatus } from '@/modules/inventory/schemas/expense.schema';
import { Revenue, RevenueDocument, RevenueCategory } from '@/modules/inventory/schemas/revenue.schema';
import { CustomerStatisticsService } from './customer-statistics.service';
import { OrderItemType } from '@/modules/order/enums/order-item-type.enum';

@Injectable()
export class StatisticsService {
  constructor(
    @InjectModel(Order.name) private orderModel: Model<OrderDocument>,
    @InjectModel(Product.name) private productModel: Model<ProductDocument>,
    @InjectModel(Package.name) private packageModel: Model<PackageDocument>,
    @InjectModel(Expense.name) private expenseModel: Model<ExpenseDocument>,
    @InjectModel(Revenue.name) private revenueModel: Model<RevenueDocument>,
    private customerStatisticsService: CustomerStatisticsService,
  ) {}

  /**
   * Helper methods for date calculations
   */
  private startOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private endOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
  }

  private startOfMonth(date: Date): Date {
    const d = new Date(date.getFullYear(), date.getMonth(), 1);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private endOfMonth(date: Date): Date {
    const d = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    d.setHours(23, 59, 59, 999);
    return d;
  }

  private startOfYear(date: Date): Date {
    const d = new Date(date.getFullYear(), 0, 1);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private endOfYear(date: Date): Date {
    const d = new Date(date.getFullYear(), 11, 31);
    d.setHours(23, 59, 59, 999);
    return d;
  }

  private subDays(date: Date, days: number): Date {
    const d = new Date(date);
    d.setDate(d.getDate() - days);
    return d;
  }

  private subMonths(date: Date, months: number): Date {
    const d = new Date(date);
    d.setMonth(d.getMonth() - months);
    return d;
  }

  private subYears(date: Date, years: number): Date {
    const d = new Date(date);
    d.setFullYear(d.getFullYear() - years);
    return d;
  }

  /**
   * Get revenue statistics for today, this month, and this year
   */
  async getRevenueStatistics() {
    const now = new Date();
    
    // Today
    const todayStart = this.startOfDay(now);
    const todayEnd = this.endOfDay(now);
    const todayRevenue = await this.calculateRevenue(todayStart, todayEnd);

    // This month
    const monthStart = this.startOfMonth(now);
    const monthEnd = this.endOfMonth(now);
    const monthRevenue = await this.calculateRevenue(monthStart, monthEnd);

    // This year
    const yearStart = this.startOfYear(now);
    const yearEnd = this.endOfYear(now);
    const yearRevenue = await this.calculateRevenue(yearStart, yearEnd);

    // Previous periods for comparison
    const yesterdayRevenue = await this.calculateRevenue(
      this.startOfDay(this.subDays(now, 1)),
      this.endOfDay(this.subDays(now, 1))
    );

    const lastMonthRevenue = await this.calculateRevenue(
      this.startOfMonth(this.subMonths(now, 1)),
      this.endOfMonth(this.subMonths(now, 1))
    );

    const lastYearRevenue = await this.calculateRevenue(
      this.startOfYear(this.subYears(now, 1)),
      this.endOfYear(this.subYears(now, 1))
    );

    return {
      today: {
        revenue: todayRevenue,
        change: todayRevenue - yesterdayRevenue,
        changePercent: yesterdayRevenue > 0 ? ((todayRevenue - yesterdayRevenue) / yesterdayRevenue) * 100 : 0,
      },
      month: {
        revenue: monthRevenue,
        change: monthRevenue - lastMonthRevenue,
        changePercent: lastMonthRevenue > 0 ? ((monthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100 : 0,
      },
      year: {
        revenue: yearRevenue,
        change: yearRevenue - lastYearRevenue,
        changePercent: lastYearRevenue > 0 ? ((yearRevenue - lastYearRevenue) / lastYearRevenue) * 100 : 0,
      },
    };
  }

  /**
   * Calculate sales (subtotal) for a date range
   */
  private async calculateRevenue(startDate: Date, endDate: Date): Promise<number> {
    const result = await this.orderModel.aggregate([
      {
        $match: {
          createdAt: {
            $gte: startDate,
            $lte: endDate,
          },
          paymentStatus: { $in: ['paid', 'completed'] }, // Only count paid orders
        },
      },
      {
        $group: {
          _id: null,
          totalRevenue: { $sum: '$subtotal' }, // Use subtotal instead of total
        },
      },
    ]);

    return result.length > 0 ? result[0].totalRevenue : 0;
  }

  /**
   * Get top selling products
   */
  async getTopSellingProducts(limit: number = 10): Promise<any[]> {
    // Get revenue for each product from orders
    const productRevenues = await this.orderModel.aggregate([
      {
        $unwind: '$items',
      },
      {
        $match: {
          'items.itemType': 'Product',
          paymentStatus: { $in: ['paid', 'completed'] },
        },
      },
      {
        $group: {
          _id: '$items.itemId',
          totalRevenue: { $sum: '$items.totalPrice' },
          totalQuantity: { $sum: '$items.quantity' },
        },
      },
      {
        $sort: { totalRevenue: -1 },
      },
      {
        $limit: limit,
      },
    ]);

    // Get product IDs from revenue results
    const productIds = productRevenues.map(rev => rev._id);

    // Fetch all products that were sold
    const products = await this.productModel
      .find({ _id: { $in: productIds } })
      .select('name price soldCount images')
      .exec();

    // Create a map for quick lookup
    const productMap = new Map(products.map(p => [p._id.toString(), p.toObject()]));
    
    return productRevenues.map(rev => {
      const product = productMap.get(rev._id.toString());
      return {
        productId: rev._id,
        name: product?.name || { en: 'Unknown', ar: 'غير معروف' },
        price: product?.price || 0,
        soldCount: product?.soldCount || 0,
        totalRevenue: rev.totalRevenue,
        totalQuantity: rev.totalQuantity,
        images: product?.images || [],
      };
    });
  }

  /**
   * Get top selling packages
   */
  async getTopSellingPackages(limit: number = 10): Promise<any[]> {
    // Get revenue for each package from orders
    const packageRevenues = await this.orderModel.aggregate([
      {
        $unwind: '$items',
      },
      {
        $match: {
          'items.itemType': 'Package',
          paymentStatus: { $in: ['paid', 'completed'] },
        },
      },
      {
        $group: {
          _id: '$items.itemId',
          totalRevenue: { $sum: '$items.totalPrice' },
          totalQuantity: { $sum: '$items.quantity' },
        },
      },
      {
        $sort: { totalRevenue: -1 },
      },
      {
        $limit: limit,
      },
    ]);

    // Get package IDs from revenue results
    const packageIds = packageRevenues.map(rev => rev._id);

    // Fetch all packages that were sold
    const packages = await this.packageModel
      .find({ _id: { $in: packageIds } })
      .select('name price soldCount images')
      .exec();

    // Create a map for quick lookup
    const packageMap = new Map(packages.map(p => [p._id.toString(), p.toObject()]));
    
    return packageRevenues.map(rev => {
      const pkg = packageMap.get(rev._id.toString());
      return {
        packageId: rev._id,
        name: pkg?.name || { en: 'Unknown', ar: 'غير معروف' },
        price: pkg?.price || 0,
        soldCount: pkg?.soldCount || 0,
        totalRevenue: rev.totalRevenue,
        totalQuantity: rev.totalQuantity,
        images: pkg?.images || [],
      };
    });
  }

  /**
   * Get top customers by orders
   */
  async getTopCustomersByOrders(limit: number = 10) {
    return this.customerStatisticsService.getTopCustomersByOrders(limit);
  }

  /**
   * Get top customers by amount
   */
  async getTopCustomersByAmount(limit: number = 10) {
    return this.customerStatisticsService.getTopCustomersByAmount(limit);
  }

  /**
   * Get total number of customers
   */
  async getTotalCustomers(): Promise<number> {
    return this.customerStatisticsService.getTotalCustomers();
  }

  /**
   * Get top states/provinces by sales
   */
  async getTopStatesBySales(limit: number = 10): Promise<any[]> {
    // First, get aggregated state data
    const result = await this.orderModel.aggregate([
      {
        $match: {
          'shippingAddress.state': { $exists: true, $ne: null },
          paymentStatus: { $in: ['paid', 'completed'] },
        },
      },
      {
        // Normalize state ID - convert to string for consistent grouping
        $addFields: {
          stateIdStr: {
            $toString: '$shippingAddress.state'
          }
        }
      },
      {
        $group: {
          _id: '$stateIdStr',
          totalRevenue: { $sum: '$subtotal' }, // Use subtotal instead of total
          orderCount: { $sum: 1 },
        },
      },
      {
        $sort: { totalRevenue: -1 },
      },
      {
        $limit: limit,
      },
    ]);

    // Get all unique state IDs
    const stateIds = result.map(item => {
      try {
        return Types.ObjectId.isValid(item._id) ? new Types.ObjectId(item._id) : item._id;
      } catch {
        return item._id;
      }
    });

    // Fetch states from database
    const StateModel = this.orderModel.db.model('State');
    const states = await StateModel.find({ _id: { $in: stateIds } }).select('name').exec();
    const stateMap = new Map(states.map(s => [s._id.toString(), s.toObject()]));

    // Combine results
    return result.map(item => {
      const stateIdStr = item._id.toString();
      const state = stateMap.get(stateIdStr);
      
      return {
        stateId: item._id,
        stateName: state?.name || { en: 'Unknown', ar: 'غير معروف' },
        totalRevenue: item.totalRevenue,
        orderCount: item.orderCount,
      };
    });
  }

  /**
   * Get order count statistics
   */
  async getOrderCountStatistics() {
    const now = new Date();
    
    const todayOrders = await this.orderModel.countDocuments({
      createdAt: {
        $gte: this.startOfDay(now),
        $lte: this.endOfDay(now),
      },
    });

    const monthOrders = await this.orderModel.countDocuments({
      createdAt: {
        $gte: this.startOfMonth(now),
        $lte: this.endOfMonth(now),
      },
    });

    const yearOrders = await this.orderModel.countDocuments({
      createdAt: {
        $gte: this.startOfYear(now),
        $lte: this.endOfYear(now),
      },
    });

    const pendingOrders = await this.orderModel.countDocuments({
      paymentStatus: 'pending',
    });

    const paidOrders = await this.orderModel.countDocuments({
      paymentStatus: { $in: ['paid', 'completed'] },
    });

    return {
      today: todayOrders,
      month: monthOrders,
      year: yearOrders,
      pending: pendingOrders,
      paid: paidOrders,
    };
  }

  /**
   * Get average order value statistics
   */
  async getAverageOrderValue() {
    const result = await this.orderModel.aggregate([
      {
        $match: {
          paymentStatus: { $in: ['paid', 'completed'] },
        },
      },
      {
        $group: {
          _id: null,
          averageOrderValue: { $avg: '$subtotal' }, // Use subtotal instead of total
          minOrderValue: { $min: '$subtotal' },
          maxOrderValue: { $max: '$subtotal' },
        },
      },
    ]);

    return result.length > 0 ? result[0] : {
      averageOrderValue: 0,
      minOrderValue: 0,
      maxOrderValue: 0,
    };
  }

  /**
   * Get sales trend (last 7 days)
   */
  async getSalesTrend(days: number = 7): Promise<any[]> {
    const now = new Date();
    const trend = [];

    for (let i = days - 1; i >= 0; i--) {
      const date = this.subDays(now, i);
      const dayStart = this.startOfDay(date);
      const dayEnd = this.endOfDay(date);

      // Calculate sales (subtotal) for this day
      const sales = await this.calculateRevenue(dayStart, dayEnd);
      const orderCount = await this.orderModel.countDocuments({
        createdAt: {
          $gte: dayStart,
          $lte: dayEnd,
        },
      });

      trend.push({
        date: dayStart.toISOString().split('T')[0],
        revenue: sales, // This is actually subtotal (sales)
        orderCount,
      });
    }

    return trend;
  }

  /**
   * Get payment method statistics
   */
  async getPaymentMethodStatistics() {
    const result = await this.orderModel.aggregate([
      {
        $match: {
          paymentStatus: { $in: ['paid', 'completed'] },
        },
      },
      {
        $group: {
          _id: '$paymentMethod',
          count: { $sum: 1 },
          totalRevenue: { $sum: '$subtotal' }, // Use subtotal instead of total
        },
      },
      {
        $sort: { totalRevenue: -1 },
      },
    ]);

    return result.map(item => ({
      paymentMethod: item._id,
      count: item.count,
      totalRevenue: item.totalRevenue,
    }));
  }

  /**
   * Calculate Cost of Goods Sold (COGS) for a date range
   */
  private async calculateCostOfGoodsSold(startDate: Date, endDate: Date): Promise<number> {
    const orders = await this.orderModel.find({
      createdAt: { $gte: startDate, $lte: endDate },
      paymentStatus: { $in: ['paid', 'completed'] },
    }).exec();

    let totalCOGS = 0;

    for (const order of orders) {
      for (const item of order.items) {
        try {
          if (item.itemType === OrderItemType.PACKAGE) {
            // For packages, calculate cost from package items
            if (item.packageItems && item.packageItems.length > 0) {
              for (const packageItem of item.packageItems) {
                const product = await this.productModel.findById(packageItem.productId).exec();
                if (product) {
                  const cost = product.factoryPrice || product.price * 0.6; // Default 60% as cost
                  totalCOGS += cost * packageItem.quantity;
                }
              }
            } else {
              // If no packageItems, use package price estimate
              const pkg = await this.packageModel.findById(item.itemId).exec();
              if (pkg) {
                const cost = pkg.price * 0.6; // Default 60% as cost
                totalCOGS += cost * item.quantity;
              }
            }
          } else {
            // For products
            const product = await this.productModel.findById(item.itemId).exec();
            if (product) {
              const cost = product.factoryPrice || product.price * 0.6; // Default 60% as cost
              totalCOGS += cost * item.quantity;
            }
          }
        } catch (error) {
        }
      }
    }

    return totalCOGS;
  }

  /**
   * Calculate total expenses for a date range
   */
  private async calculateExpenses(startDate: Date, endDate: Date): Promise<number> {
    const expenses = await this.expenseModel.find({
      expenseDate: { $gte: startDate, $lte: endDate },
      status: ExpenseStatus.APPROVED,
    }).exec();

    return expenses.reduce((sum, expense) => sum + expense.amount, 0);
  }

  /**
   * Calculate Profit Statistics for a date range
   */
  async calculateProfitStatistics(startDate: Date, endDate: Date) {
    const [revenue, cogs, expenses] = await Promise.all([
      this.calculateRevenue(startDate, endDate),
      this.calculateCostOfGoodsSold(startDate, endDate),
      this.calculateExpenses(startDate, endDate),
    ]);

    const grossProfit = revenue - cogs;
    const netProfit = grossProfit - expenses;
    const profitMargin = revenue > 0 ? (netProfit / revenue) * 100 : 0;
    const grossProfitMargin = revenue > 0 ? (grossProfit / revenue) * 100 : 0;

    return {
      revenue,
      costOfGoodsSold: cogs,
      expenses,
      grossProfit,
      netProfit,
      profitMargin,
      grossProfitMargin,
    };
  }

  /**
   * Get Profit Statistics with date filtering (day, month, year)
   */
  async getProfitStatistics(period?: 'day' | 'month' | 'year', startDate?: Date, endDate?: Date) {
    let start: Date;
    let end: Date;

    const now = new Date();

    if (startDate && endDate) {
      // Custom date range
      start = this.startOfDay(startDate);
      end = this.endOfDay(endDate);
    } else if (period === 'day') {
      start = this.startOfDay(now);
      end = this.endOfDay(now);
    } else if (period === 'month') {
      start = this.startOfMonth(now);
      end = this.endOfMonth(now);
    } else if (period === 'year') {
      start = this.startOfYear(now);
      end = this.endOfYear(now);
    } else {
      // Default: current month
      start = this.startOfMonth(now);
      end = this.endOfMonth(now);
    }

    const current = await this.calculateProfitStatistics(start, end);

    // Calculate previous period for comparison
    let previousStart: Date;
    let previousEnd: Date;

    if (period === 'day') {
      const yesterday = this.subDays(now, 1);
      previousStart = this.startOfDay(yesterday);
      previousEnd = this.endOfDay(yesterday);
    } else if (period === 'month') {
      const lastMonth = this.subMonths(now, 1);
      previousStart = this.startOfMonth(lastMonth);
      previousEnd = this.endOfMonth(lastMonth);
    } else if (period === 'year') {
      const lastYear = this.subYears(now, 1);
      previousStart = this.startOfYear(lastYear);
      previousEnd = this.endOfYear(lastYear);
    } else {
      const lastMonth = this.subMonths(now, 1);
      previousStart = this.startOfMonth(lastMonth);
      previousEnd = this.endOfMonth(lastMonth);
    }

    const previous = await this.calculateProfitStatistics(previousStart, previousEnd);

    return {
      period: {
        type: period || 'month',
        startDate: start,
        endDate: end,
      },
      current,
      previous,
      comparison: {
        revenueChange: current.revenue - previous.revenue,
        revenueChangePercent: previous.revenue > 0 ? ((current.revenue - previous.revenue) / previous.revenue) * 100 : 0,
        netProfitChange: current.netProfit - previous.netProfit,
        netProfitChangePercent: previous.netProfit !== 0 ? ((current.netProfit - previous.netProfit) / Math.abs(previous.netProfit)) * 100 : 0,
        profitMarginChange: current.profitMargin - previous.profitMargin,
      },
    };
  }

  /**
   * Get date range from period type
   */
  private getDateRangeFromPeriod(period?: 'day' | 'month' | 'year', startDate?: Date, endDate?: Date): { start: Date; end: Date } {
    const now = new Date();

    if (startDate && endDate) {
      return {
        start: this.startOfDay(startDate),
        end: this.endOfDay(endDate),
      };
    }

    if (period === 'day') {
      return {
        start: this.startOfDay(now),
        end: this.endOfDay(now),
      };
    }

    if (period === 'month') {
      return {
        start: this.startOfMonth(now),
        end: this.endOfMonth(now),
      };
    }

    if (period === 'year') {
      return {
        start: this.startOfYear(now),
        end: this.endOfYear(now),
      };
    }

    // Default: current month
    return {
      start: this.startOfMonth(now),
      end: this.endOfMonth(now),
    };
  }

  /**
   * Get comprehensive Ecommerce Statistics with date filtering
   */
  async getEcommerceStatistics(period?: 'day' | 'month' | 'year', startDate?: Date, endDate?: Date) {
    const dateRange = this.getDateRangeFromPeriod(period, startDate, endDate);

    const [
      profitStats,
      revenueStats,
      topProducts,
      topPackages,
      topCustomersByOrders,
      topCustomersByAmount,
      topStates,
      totalCustomers,
      totalOrders,
      totalProducts,
      totalPackages,
      orderCountStats,
      averageOrderValue,
      salesTrend,
      paymentMethodStats,
    ] = await Promise.all([
      this.calculateProfitStatistics(dateRange.start, dateRange.end),
      this.getRevenueStatistics(),
      this.getTopSellingProducts(10),
      this.getTopSellingPackages(10),
      this.getTopCustomersByOrders(10),
      this.getTopCustomersByAmount(10),
      this.getTopStatesBySales(10),
      this.getTotalCustomers(),
      this.orderModel.countDocuments({ paymentStatus: { $in: ['paid', 'completed'] } }),
      this.productModel.countDocuments(),
      this.packageModel.countDocuments(),
      this.getOrderCountStatistics(),
      this.getAverageOrderValue(),
      this.getSalesTrend(7),
      this.getPaymentMethodStatistics(),
    ]);

    return {
      period: {
        type: period || 'month',
        startDate: dateRange.start,
        endDate: dateRange.end,
      },
      profit: profitStats,
      revenue: revenueStats,
      orderCount: orderCountStats,
      averageOrderValue,
      salesTrend,
      paymentMethods: paymentMethodStats,
      topProducts,
      topPackages,
      topCustomersByOrders,
      topCustomersByAmount,
      topStates,
      totals: {
        customers: totalCustomers,
        orders: totalOrders,
        products: totalProducts,
        packages: totalPackages,
      },
    };
  }

  /**
   * Get comprehensive dashboard statistics
   */
  async getDashboardStatistics() {
    const [
      revenueStats,
      profitStats,
      topProducts,
      topPackages,
      topCustomersByOrders,
      topCustomersByAmount,
      topStates,
      totalCustomers,
      totalOrders,
      totalProducts,
      totalPackages,
      orderCountStats,
      averageOrderValue,
      salesTrend,
      paymentMethodStats,
    ] = await Promise.all([
      this.getRevenueStatistics(),
      this.getProfitStatistics('month'), // Default to current month
      this.getTopSellingProducts(10),
      this.getTopSellingPackages(10),
      this.getTopCustomersByOrders(10),
      this.getTopCustomersByAmount(10),
      this.getTopStatesBySales(10),
      this.getTotalCustomers(),
      this.orderModel.countDocuments({ paymentStatus: { $in: ['paid', 'completed'] } }),
      this.productModel.countDocuments(),
      this.packageModel.countDocuments(),
      this.getOrderCountStatistics(),
      this.getAverageOrderValue(),
      this.getSalesTrend(7),
      this.getPaymentMethodStatistics(),
    ]);

    return {
      revenue: revenueStats,
      profit: profitStats,
      orderCount: orderCountStats,
      averageOrderValue,
      salesTrend,
      paymentMethods: paymentMethodStats,
      topProducts,
      topPackages,
      topCustomersByOrders,
      topCustomersByAmount,
      topStates,
      totals: {
        customers: totalCustomers,
        orders: totalOrders,
        products: totalProducts,
        packages: totalPackages,
      },
    };
  }

  /**
   * Get order statistics by date range
   */
  async getOrderStatisticsByDateRange(startDate: Date, endDate: Date) {
    const orders = await this.orderModel.find({
      createdAt: {
        $gte: startDate,
        $lte: endDate,
      },
    });

    const totalSales = orders
      .filter(o => ['paid', 'completed'].includes(o.paymentStatus))
      .reduce((sum, o) => sum + (o.subtotal || 0), 0); // Use subtotal instead of total

    const orderCount = orders.length;
    const paidOrderCount = orders.filter(o => ['paid', 'completed'].includes(o.paymentStatus)).length;

    return {
      totalRevenue: totalSales, // This is actually subtotal (sales)
      orderCount,
      paidOrderCount,
      averageOrderValue: paidOrderCount > 0 ? totalSales / paidOrderCount : 0,
    };
  }

  /**
   * Recalculate all statistics from scratch
   * This will:
   * 1. Recalculate soldCount for all products and packages
   * 2. Recalculate customer statistics from all orders
   */
  async recalculateAllStatistics(): Promise<{
    productsUpdated: number;
    packagesUpdated: number;
    customersUpdated: number;
    message: string;
  }> {

    // Step 1: Reset all soldCount to 0
    await this.productModel.updateMany({}, { $set: { soldCount: 0 } });
    await this.packageModel.updateMany({}, { $set: { soldCount: 0 } });

    // Step 2: Get all paid/completed orders
    const orders = await this.orderModel.find({
      paymentStatus: { $in: ['paid', 'completed'] },
    }).exec();


    // Step 3: Recalculate soldCount for products and packages
    const productSoldCount = new Map<string, number>();
    const packageSoldCount = new Map<string, number>();

    for (const order of orders) {
      for (const item of order.items) {
        if (item.itemType === OrderItemType.PACKAGE) {
          const packageId = item.itemId.toString();
          const currentCount = packageSoldCount.get(packageId) || 0;
          packageSoldCount.set(packageId, currentCount + item.quantity);

          // Also update individual products within packages
          if (item.packageItems && item.packageItems.length > 0) {
            for (const packageItem of item.packageItems) {
              const productId = packageItem.productId.toString();
              const currentProductCount = productSoldCount.get(productId) || 0;
              productSoldCount.set(productId, currentProductCount + packageItem.quantity);
            }
          }
        } else {
          const productId = item.itemId.toString();
          const currentCount = productSoldCount.get(productId) || 0;
          productSoldCount.set(productId, currentCount + item.quantity);
        }
      }
    }

    // Step 4: Update soldCount in database
    let productsUpdated = 0;
    for (const [productId, count] of productSoldCount.entries()) {
      try {
        await this.productModel.findByIdAndUpdate(productId, { $set: { soldCount: count } });
        productsUpdated++;
      } catch (error) {
      }
    }

    let packagesUpdated = 0;
    for (const [packageId, count] of packageSoldCount.entries()) {
      try {
        await this.packageModel.findByIdAndUpdate(packageId, { $set: { soldCount: count } });
        packagesUpdated++;
      } catch (error) {
      }
    }


    // Step 5: Recalculate customer statistics
    // First, delete all existing customer statistics
    await this.customerStatisticsService.deleteAll();

    // Then, recalculate from all orders
    let customersUpdated = 0;
    const customerMap = new Map<string, {
      phone: string;
      fullName?: string;
      orderCount: number;
      totalAmount: number;
      state?: Types.ObjectId;
      country?: Types.ObjectId;
      city?: string;
      address?: string;
      firstOrderDate?: Date;
      lastOrderDate?: Date;
    }>();

    for (const order of orders) {
      if (order.shippingAddress?.phone) {
        const phone = order.shippingAddress.phone;
        const existing = customerMap.get(phone);

        if (existing) {
          existing.orderCount += 1;
          existing.totalAmount += (order.subtotal || 0); // Use subtotal instead of total (sales amount)
          if (order.createdAt && (!existing.lastOrderDate || order.createdAt > existing.lastOrderDate)) {
            existing.lastOrderDate = order.createdAt;
          }
          if (order.createdAt && (!existing.firstOrderDate || order.createdAt < existing.firstOrderDate)) {
            existing.firstOrderDate = order.createdAt;
          }
        } else {
          customerMap.set(phone, {
            phone,
            fullName: order.shippingAddress.fullName,
            orderCount: 1,
            totalAmount: order.subtotal || 0, // Use subtotal instead of total (sales amount)
            state: order.shippingAddress.state ? (typeof order.shippingAddress.state === 'string' ? new Types.ObjectId(order.shippingAddress.state) : order.shippingAddress.state) : undefined,
            country: order.shippingAddress.country ? (typeof order.shippingAddress.country === 'string' ? new Types.ObjectId(order.shippingAddress.country) : order.shippingAddress.country) : undefined,
            city: order.shippingAddress.city,
            address: order.shippingAddress.address,
            firstOrderDate: order.createdAt,
            lastOrderDate: order.createdAt,
          });
        }
      }
    }

    // Step 6: Save customer statistics
    for (const customerData of customerMap.values()) {
      try {
        await this.customerStatisticsService.createOrUpdateCustomer(customerData);
        customersUpdated++;
      } catch (error) {
      }
    }


    return {
      productsUpdated,
      packagesUpdated,
      customersUpdated,
      message: `Successfully recalculated statistics: ${productsUpdated} products, ${packagesUpdated} packages, ${customersUpdated} customers`,
    };
  }
}

